import React from 'react';
import PropTypes from 'prop-types';
import { pick, times, map, is } from 'ramda';

import { graph } from './Graph.less';

const qol15Dsections = [
  'mobility',
  'vision',
  'hearing',
  'breathing',
  'sleeping',
  'eating',
  'speech',
  'excretion',
  'usualActivities',
  'mentalFunction',
  'discomfortAndSymptoms',
  'depression',
  'distress',
  'vitality',
  'sexualActivity',
];

const eq5dSections = ['mobility', 'selfcare', 'usualActivities', 'painDiscomfort', 'anxietyDepression'];

const arcStrokeWidth = 18;
const arcBaseRadius = 48;
const rimGutter = 2;
const levels = 5;

const colors = ['#1b978f', '#e8bcb1', '#e99c87', '#e9734f', '#e84a1b'];

const colorUnfilled = '#9bd3d3';
const colorBlank = '#ececec';

const degToRadians = deg => deg * (Math.PI / 180);

const polarToCartesian = (cX, cY, radius, angle) => ({
  x: cX + radius * Math.cos(degToRadians(angle)),
  y: cY + radius * Math.sin(degToRadians(angle)),
});

const RenderPath = ({ color, d }) => (
  <path fill="none" strokeLinecap="butt" stroke={color} strokeWidth={arcStrokeWidth} d={d} />
);

RenderPath.propTypes = {
  color: PropTypes.string,
  d: PropTypes.string,
};

const makeArcDescriber = (x, y) => (radius, startAngle, endAngle) => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(' ');
};

const describer = makeArcDescriber(145, 150);

/*
  Graph render function itself
 */

const getFanColor = (fid, val) => {
  if (fid === 0 && val < 1) return colorUnfilled;
  if (fid === 0 && val >= 1) return colors[0];
  if (fid > 0 && val > fid) return colors[fid];
  return colorBlank;
};

const sectionTitle = (t, s) => (s ? `${t}.${s}.title` : null);
const opt = (t, s, o) => (s && o ? `${t}.${s}.${o}` : null);

const QoLGraph = ({ documentType, sections, calculatedValue, localeMessage }) => {
  const sectionNames = documentType === 'eq5d' ? eq5dSections : qol15Dsections;
  const pickSections = pick(sectionNames);
  const pickedSections = pickSections(sections);
  const numberOfSections = sectionNames.length;
  const lengthOfArc = 360 / numberOfSections;
  const padding = 20 / 15; //20 / numberOfSections;

  const getSectionValue = k => (k && pickedSections[k] ? pickedSections[k] : -1);
  const getIndex = k => sectionNames.indexOf(k);

  return (
    <svg className={graph} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 290 300">
      <text x="145" y="150" textAnchor="middle" dy="6" fontSize="24">
        {is(Number, calculatedValue) && calculatedValue.toFixed(3)}
      </text>
      {map(s => (
        <g key={`arc_${s}`}>
          {times(f => (
            <RenderPath
              key={`arc_${s}_${f}`}
              color={getFanColor(f, getSectionValue(s))}
              d={describer(
                arcBaseRadius + (arcStrokeWidth + rimGutter) * f,
                getIndex(s) * lengthOfArc + padding,
                (getIndex(s) + 1) * lengthOfArc,
              )}
            />
          ))(levels)}
          <title>
            {getSectionValue(s) > 0
              ? `${localeMessage(sectionTitle(documentType, s))} (${getSectionValue(s)}): ${localeMessage(
                  opt(documentType, s, getSectionValue(s)),
                )}`
              : `${localeMessage(sectionTitle(documentType, s))} (-)`}
          </title>
        </g>
      ))(sectionNames)}
    </svg>
  );
};

QoLGraph.propTypes = {
  documentType: PropTypes.string,
  sections: PropTypes.object,
  calculatedValue: PropTypes.number,
  localeMessage: PropTypes.func,
};

export default QoLGraph;
